import { FollowType, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { withHighlightedHeadline } from "~/components/content/content-item-render/hoc";
import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import type { sectionTopLeadPrimaryContentItemContent$key } from "~/queries/__generated__/sectionTopLeadPrimaryContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Headline,
  LeadingImage,
  LinkHighlightArea,
  Primary,
  Rest,
  Section,
  SectionContainer,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledHomeFollowButton,
  SubHeadline,
  Summary,
  Topic,
  TopicContainer,
} from "./styles";

export type Props = {
  children?: ReactNode;
  reference: sectionTopLeadPrimaryContentItemContent$key;
  withBookmark?: boolean;
  withComment?: boolean;
  withSection?: boolean;
  withTopic?: boolean;
} & ContentItemRenderVariantProps;

const Component = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      className,
      customQueryParameters,
      onClick,
      reference: reference_,
      withBookmark = false,
      withComment = false,
      withSection = false,
      withTopic = false,
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment sectionTopLeadPrimaryContentItemContent on Content {
          entityId
          topics {
            ...followButtonBase
          }
          ...entityLink
          ...contentCoverImageContent @arguments(withLandscape250x99: true, withSize540x360: true)
          ...hooksUseContentSponsorContent
          ...hooksContentItemProvidersContent
            @arguments(
              withSections: true
              withTopicLink: true
              withHeadline: true
              withSummary: true
              withSubHeadline: true
              withActionBar: true
              withSponsorTag: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(content, { type: "main" });

    const contentSponsor = useContentSponsor(content);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const topic = first(content?.topics ?? []);

    return (
      <Container className={className} ref={reference}>
        <LeadingImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <StyledContentCoverImage
              reference={content}
              responsiveVariants={{
                desktopUp: "landscape250x99",
                mobileUp: "size540x360",
                tabletUp: "landscape250x99",
              }}
            />
          </StyledEntityLink>
        </LeadingImage>
        <Primary>
          {withTopic && (
            <TopicContainer>
              <Topic>{providers.topicLink?.()}</Topic>
              {topic && (
                <StyledHomeFollowButton
                  buttonVariant="square"
                  reference={topic}
                  source="HP Section Block"
                  type={FollowType.Topic}
                />
              )}
            </TopicContainer>
          )}
          {withSection && (
            <SectionContainer>
              <Section>{providers.sections()}</Section>
            </SectionContainer>
          )}
          <StyledEntityLink
            query={customQueryParameters}
            reference={content}
            onClick={() => onClick?.(content.entityId)}
          >
            <LinkHighlightArea>
              <Headline>
                {providers.headline({
                  hideTooltip: true,
                  preferSocialHeadline: true,
                })}
              </Headline>
              <Summary>
                {providers.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
              <SubHeadline>{providers.subHeadline()}</SubHeadline>
            </LinkHighlightArea>
          </StyledEntityLink>
          {isSponsorArticle ? (
            providers.sponsorTag?.({ variant: "home" })
          ) : (
            <ActionBar>
              {providers.actionBar({
                commentVariant: "reverse",
                isBookmarkVisible: withBookmark ?? false,
                isCommentsVisible: withComment ?? false,
              })}
            </ActionBar>
          )}
        </Primary>
        <Rest>{children}</Rest>
      </Container>
    );
  },
);

Component.displayName = "ContentItemSectionTopLeadPrimary";

export const ContentItemSectionTopLeadPrimary = withHighlightedHeadline(Component);
